import type { TaxCategory } from "@fiscalo/shared-types";

/**
 * The IVA rates an invoice line may carry — the single source of truth.
 *
 * WHY A LIST AND NOT A MAP.
 *
 * `TAX_RATE_BY_CATEGORY` keys the rate by TaxCategory, which works only while
 * every category has exactly one rate. It does not: the Res. DGI 1222/008
 * automotora regime needs 2,2% for vehicles of 4+ wheels and 2,75% for
 * two-wheelers, and BOTH are category `vat_other` (DGI's "Gravado a Otra
 * Tasa", IndFact 4). A category-keyed map structurally cannot hold two rates
 * for one category, which is why the rate moves onto the option.
 *
 * The category still decides the CFE shape — `vat_other` maps to IndFact 4 and
 * fills MntNetoIVAOtra / MntIVAOtra. Verified against DGI ePrueba on
 * 2026-09-01: a 111 with the full sale price in MntNetoIVAOtra and 2,2% of it
 * in MntIVAOtra came back Estado=AE. Nothing in the CFE declares the rate —
 * Totales carries only amounts — so the rate lives here and only here.
 *
 * THE 2,2% IS NOT A RATE DGI KNOWS. Art. 14 Tít. 10 TO 2023 taxes only the
 * "valor agregado en esta etapa"; art. 107 Dec. 220/998 fixes that at 10% of
 * the sale price (12,5% for motos). 22% of 10% is the 2,2% automotoras quote.
 */

export type SpecialRegime = "automotoras_1222_008";

export interface TaxOption {
  /** Stable id, used in the editor's select. NOT persisted: the line stores
   *  category + rate, which identifies the option unambiguously. */
  id: string;
  labelKey: string;
  /** Spanish default, matching the inline-fallback convention in this app. */
  fallback: string;
  /** Drives the CFE IndFact and which Totales bucket the amount lands in. */
  category: TaxCategory;
  /** Fraction, not percent: 0.22 is 22%. */
  rate: number;
  /** When set, the option is offered only to tenants under that regime. */
  requiresRegime?: SpecialRegime;
}

/** Offered to every tenant. Order is the order shown. */
const STANDARD_OPTIONS: TaxOption[] = [
  { id: "exempt", labelKey: "invoices.new.taxOptionExempt", fallback: "Exento - 0%", category: "exempt", rate: 0 },
  { id: "vat_basic", labelKey: "invoices.new.taxOptionVatBasic", fallback: "IVA básico - 22%", category: "vat_basic", rate: 0.22 },
  { id: "vat_minimum", labelKey: "invoices.new.taxOptionVatMinimum", fallback: "IVA mínimo - 10%", category: "vat_minimum", rate: 0.1 },
  { id: "other", labelKey: "invoices.new.taxOptionNotTaxable", fallback: "No gravado", category: "other", rate: 0 },
  { id: "export", labelKey: "invoices.new.taxOptionExport", fallback: "Exportación y asimiladas - 0%", category: "export", rate: 0 },
];

/**
 * Régimen-gated options. APPENDED to the standard five rather than replacing
 * them — an automotora still sells parts and services at 22%, so it needs the
 * whole list plus these.
 */
const REGIME_OPTIONS: TaxOption[] = [
  {
    id: "automotor_2_2",
    labelKey: "invoices.new.taxOptionAutomotor",
    fallback: "IVA automotor - 2,2%",
    category: "vat_other",
    rate: 0.022,
    requiresRegime: "automotoras_1222_008",
  },
  {
    id: "moto_2_75",
    labelKey: "invoices.new.taxOptionMoto",
    fallback: "IVA motos - 2,75%",
    category: "vat_other",
    rate: 0.0275,
    requiresRegime: "automotoras_1222_008",
  },
];

export const ALL_TAX_OPTIONS: TaxOption[] = [
  ...STANDARD_OPTIONS,
  ...REGIME_OPTIONS,
];

/** What this tenant may pick. A null regime gets exactly the standard five. */
export function taxOptionsForRegime(
  specialRegime: SpecialRegime | null | undefined,
): TaxOption[] {
  return ALL_TAX_OPTIONS.filter(
    (o) => o.requiresRegime == null || o.requiresRegime === specialRegime,
  );
}

export function findTaxOption(id: string | null | undefined): TaxOption | undefined {
  if (!id) return undefined;
  return ALL_TAX_OPTIONS.find((o) => o.id === id);
}

/**
 * The rate for a line.
 *
 * Prefers the option id. Falls back to the category ONLY when there is no id —
 * which is every invoice line written before this existed, and is why
 * TAX_RATE_BY_CATEGORY survives: those lines must keep computing exactly the
 * tax they always did.
 */
export function resolveTaxRate(
  taxOptionId: string | null | undefined,
  category: TaxCategory,
  fallbackByCategory: Partial<Record<TaxCategory, number>>,
): number {
  const option = findTaxOption(taxOptionId);
  if (option) return option.rate;
  return fallbackByCategory[category] ?? 0;
}

/**
 * Recover the option a stored line was written with.
 *
 * The persisted line carries `tax.category` and `tax.rate` (TaxInfo already
 * had an optional rate, so nothing new is stored). That pair is enough to
 * identify the option, which is why no taxOptionId is persisted anywhere:
 * category alone is ambiguous for vat_other, but category + rate is not.
 *
 * Falls back to the category's standard option when the rate is missing —
 * every line written before tax options existed.
 */
export function findTaxOptionByCategoryAndRate(
  category: TaxCategory,
  rate: number | null | undefined,
): TaxOption | undefined {
  if (rate != null) {
    const exact = ALL_TAX_OPTIONS.find(
      (o) => o.category === category && Math.abs(o.rate - rate) < 1e-9,
    );
    if (exact) return exact;
  }
  return defaultOptionForCategory(category);
}

/** The default option for a category, for seeding a line from a catalog item
 *  that stores only a category. Standard options win so a plain `vat_other`
 *  catalog row does not silently become an automotora rate. */
export function defaultOptionForCategory(
  category: TaxCategory,
): TaxOption | undefined {
  return STANDARD_OPTIONS.find((o) => o.category === category);
}

/**
 * The rate to apply when a catalog item lands on an invoice line.
 *
 * Order: the row's stored rate, but ONLY if that option is still offered to
 * this tenant; then the category's standard option; then the legacy category
 * map.
 *
 * THE RE-VALIDATION IS THE POINT. A tenant can be set to Automotoras, have
 * twenty vehicles catalogued at 2,2%, and then have the régimen REMOVED —
 * misclassified, or they stopped dealing vehicles. Those rows still hold
 * 0.022. Without this check every one of them would keep applying a rate the
 * tenant is no longer entitled to: a wrong rate on a legal document, produced
 * silently, from data that was correct when it was written.
 *
 * The catalog row is NOT rewritten — they may be re-classified back, and
 * destroying someone's catalogue on a profile toggle is worse than falling
 * back for as long as the régimen is off.
 */
export function resolveCatalogItemRate(
  category: TaxCategory,
  storedRate: number | null | undefined,
  specialRegime: SpecialRegime | null | undefined,
  fallbackByCategory: Partial<Record<TaxCategory, number>>,
): number | undefined {
  if (storedRate != null) {
    // Number() first: the column is numeric(6,4) and TypeORM hands decimals
    // back as STRINGS ("0.0220"). The comparison below would coerce anyway,
    // but the RETURN would not — and a string reaching line.taxRate ends up
    // in the CFE payload's tax.rate, where a JSON number belongs.
    const rate = Number(storedRate);
    const stillOffered =
      Number.isFinite(rate) &&
      taxOptionsForRegime(specialRegime).some(
        (o) => o.category === category && Math.abs(o.rate - rate) < 1e-9,
      );
    if (stillOffered) return rate;
  }
  const standard = defaultOptionForCategory(category);
  if (standard) return standard.rate;
  return fallbackByCategory[category];
}
